/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { img_chevron_down } from "designsystem/assets/img_icons";
import { SelectBox } from "designsystem/components/select-box";
import { filterCards } from "utils/filters";
import { Icon } from "designsystem";
import { navigate } from "gatsby";
import { CardContact } from "designsystem/components/card";
import { useSupportResourceSet } from "hooks/resource-set";
import { InfoHelp } from "designsystem/components/info-box";
import { ButtonText } from "designsystem/atoms/button";
import { buildFaqItem, sortByOrder } from "utils/support/faq";
import { notUndefined } from "utils";
import { FaqItemTest } from "components/faq";
import { PaginationTest } from "components/pagination";
import { formatHeadline } from "designsystem/utils/text";
import { SmallTitle } from "designsystem/atoms/typography";

interface SupportContainerProps {
  components?: any;
  infoComponent?: boolean;
  showPaginationComponent?: boolean;
  selectItemComponent?: any;
  title: string;
  btnText?: string;
  svgIcon?: string;
  className?: string;
}

const SupportContainer: React.FC<SupportContainerProps> = ({
  components,
  infoComponent,
  showPaginationComponent,
  selectItemComponent,
  title,
  btnText,
  svgIcon,
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedKeyword, setSelectedKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  // using with didomyCountry prop
  //const [isInitialCountrySet, setIsInitialCountrySet] = useState(false);

  /** START - Logic for FAQ section (FAQ Items + Pagination) */
  // list of FAQ Items + Pagination
  const faqItems = (components || [])
    .map(buildFaqItem)
    .filter(notUndefined)
    .sort(sortByOrder);

  // Filter faqItems based on the selected keyword
  const filteredFaqItems = filterCards(faqItems, null, selectedKeyword);

  const itemsPerPage = 5;
  const totalPages = Math.ceil(filteredFaqItems.length / itemsPerPage);

  // Pagination handlers
  const goToNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const goToPrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  // Calculating Items for Current Page
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const filteredFaqPaginationItems = filteredFaqItems.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  // Function to find the page number of a specific FAQ item
  const getPageForCopiedFaqItem = (slug: string): number => {
    const index = filteredFaqItems.findIndex((item) => `${item.slug}` === slug);
    return index === -1 ? 1 : Math.floor(index / itemsPerPage) + 1;
  };

  // Update currentPage based on URL hash
  useEffect(() => {
    // Read the 'topic' query papameter from URL and set the selectKeyword state
    const params = new URLSearchParams(location.search);
    const topicFromURL = params.get("topic");
    const pageFromURL = params.get("page");
    const hash = window.location.hash;
    const slug = hash.replace("&faq-item=#", "");

    // Set the selected keyword from URL, if present
    if (topicFromURL) {
      setSelectedKeyword(topicFromURL);
    }

    // Calculate and set the current page for the FAQ item
    let page = 1; // Default to first page
    if (pageFromURL) {
      page = parseInt(pageFromURL, 10);
    } else if (slug && faqItems.length > 0) {
      page = getPageForCopiedFaqItem(slug);
    }
    setCurrentPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /** END - Logic for FAQ section (FAQ Items + Pagination) */

  const [
    info_infotext,
    info_tech,
    info_yourprofile,
    info_transactions,
    info_invoices
  ] = useSupportResourceSet([
    "support.contact.infotext",
    "support.contact.tech",
    "support.contact.yourprofile",
    "support.contact.transactions",
    "support.contact.invoices"
  ]);

  const infoData = [
    info_tech,
    info_yourprofile,
    info_transactions,
    info_invoices
  ];

  const toggleAccordionBox = () => {
    setIsOpen(!isOpen);
  };

  const toggleCloseBox = () => {
    setIsOpen(false);
  };

  //(window as any)?.didomiCountry;
  // using with didomyCountry prop
  // useEffect(() => {
  //   if (!isInitialCountrySet) {
  //     const didomiCountryCode = (window as any)?.didomiCountry;

  //     if (didomiCountryCode && countryCodeToNameMap[didomiCountryCode]) {
  //       setSelectedCountry(countryCodeToNameMap[didomiCountryCode]);
  //       setIsInitialCountrySet(true);
  //     } else {
  //       const params = new URLSearchParams(location.search);
  //       const countryFromURL = params.get("country");
  //       if (countryFromURL && countryCodeToNameMap[countryFromURL]) {
  //         setSelectedCountry(countryFromURL);
  //         setIsInitialCountrySet(true);
  //       }
  //     }
  //   }

  // }, [isInitialCountrySet]);

  // Update selectedCountry based on URL without didomyCountry prop
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const country = params.get("country") || "";
    setSelectedCountry(country);
  }, [setSelectedCountry]);

  // Change URL based on selected country
  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const country = event.target.value;
    setSelectedCountry(country);

    const params = new URLSearchParams(location.search);
    if (country === "") {
      params.delete("country");
    } else {
      params.set("country", country);
      params.delete("topic");
      params.delete("page");
      params.delete("faq-item");
    }

    // Only include the search string in the URL if there are any query parameters
    const search = params.toString() ? `?${params.toString()}` : "";
    navigate(`${location.pathname}${search}`, { replace: true });
  };

  // Update selectedKeyword and add topic parameter in URL based on user selection
  const handleKeywordChange = (keyword: string) => {
    // Update selectedKeyword based on user selection
    setSelectedKeyword(keyword);

    // Reset to first page
    setCurrentPage(1);

    // Update URL with the selected keyword/topic
    const params = new URLSearchParams(location.search);
    if (keyword === "") {
      params.delete("topic");
    } else {
      params.set("topic", keyword);
      params.delete("page");
      params.delete("faq-item");
      params.delete("country");
    }

    // Preserve other query parameters, if any
    const search = params.toString() ? `?${params.toString()}` : "";
    navigate(`${location.pathname}${search}`, { replace: true });
  };

  // Filter components based on the selected country
  const filteredComponents = filterCards(components, selectedCountry, null);

  return (
    <div
      className={` flex flex-col items-center gap-6 bg-nexi-colorPalette-neutral-100 w-full p-12 xs:p-6 rounded-lg ${className}`}
    >
      <div className=" text-center">
        {/* SVG icon support */}
        {svgIcon && <Icon type={svgIcon} />}

        <SmallTitle
          className=" leading-10 py-2"
          dangerouslySetInnerHTML={formatHeadline(title)}
        />
      </div>

      <ButtonText
        btnText={btnText}
        svgIcon={img_chevron_down}
        isOpen={isOpen}
        handleBtnClick={toggleAccordionBox}
      />

      {/* Accordion Box for Select Country Component, Conditional rendering based on the presence of selectItemComponent */}
      {isOpen && selectItemComponent && (
        // we can transform it to switch conditition if want to support many references from Contentful! (For now is one - __typename ContentfulSelectItems)
        <div className="flex flex-col items-center gap-3 my-6">
          <SelectBox
            data={selectItemComponent}
            selectedCountry={selectedCountry}
            selectedKeyword={selectedKeyword}
            handleCountryChange={handleCountryChange}
            handleKeywordChange={handleKeywordChange}
            icon={<Icon type="navigation_location" />}
          />
        </div>
      )}
      {/* Accordion rest of boxes, render other components */}
      {isOpen && filteredComponents && filteredFaqItems && (
        <div className=" grid grid-flow-row gap-6 w-full xs:grid-cols-1 sm:grid-cols-2 xxl:grid-cols-2">
          {infoComponent && selectedCountry !== "" && (
            <div className="xs:col-span-1 sm:col-span-2 xxl:col-span-2">
              <InfoHelp
                infotext={info_infotext}
                data={infoData}
                setIcon={true}
              />
            </div>
          )}
          {filteredComponents &&
            filteredComponents?.map((component) => {
              switch (component?.__typename) {
                case "ContentfulCardContact":
                  return (
                    component?.id && (
                      <CardContact
                        key={component?.id}
                        data={component}
                        selectedCountry={selectedCountry}
                      />
                    )
                  );
                // case "ContentfulFormContact":
                //   return (
                //     component?.id && (
                //       <div key={component?.id} className="">
                //         {component.title}
                //       </div>
                //     )
                //   );
                // new case if we have a new content model from Contenful
                default:
                  return <></>;
              }
            })}
          {selectedKeyword && (
            <ul className="xs:col-span-1 sm:col-span-2 xxl:col-span-2 w-full mx-auto bg-nexi-colorPalette-neutral-92 bg-opacity-10 text-nexi-colorPalette-neutral-50 border-0 rounded-lg [&>*:nth-child(5)]:border-b-0">
              {filteredFaqItems &&
                filteredFaqPaginationItems?.map((faqItem) => {
                  switch (faqItem?.__typename) {
                    // FAQ Items
                    case "ContentfulFaqItem":
                      return (
                        faqItem?.id && (
                          <FaqItemTest
                            key={faqItem?.id}
                            item={faqItem}
                            selectedKeyword={selectedKeyword}
                            currentPage={currentPage}
                          />
                        )
                      );

                    // new case if we have a new content model from Contenful
                    default:
                      return <></>;
                  }
                })}
              {showPaginationComponent && (
                <PaginationTest
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onNext={goToNextPage}
                  onPrev={goToPrevPage}
                />
              )}
            </ul>
          )}
          <ButtonText
            btnText={"CLOSE"}
            svgIcon={img_chevron_down}
            isOpen={isOpen}
            handleBtnClick={toggleCloseBox}
            className=" xs:col-span-1 sm:col-span-2 xxl:col-span-2 mx-auto"
          />
        </div>
      )}
    </div>
  );
};

export default SupportContainer;
